import { paginateProductInfoByAdGroup, useContentfulClient } from "@expert/contentful";
import { getLogger } from "@expert/logging";
import { type QueryFunctionContext, type UndefinedInitialDataOptions, useQuery } from "@tanstack/react-query";
import type { ContentfulClientApi } from "contentful";
import { getGamificationApiClient } from "@/sales-tools/home-product/api/api";
import { userCache } from "@/shared-utils";

interface ChecklistSummaryQueryProps
    extends Partial<
        UndefinedInitialDataOptions<
            ChecklistSummaryStats | undefined,
            Error,
            ChecklistSummaryStats | undefined,
            ChecklistSummaryStatsKey
        >
    > {
    params: ChecklistSummaryStatsParams;
}

type ChecklistSummaryStatsParams = {
    start: string;
    end: string;
    adGroups: string[];
};

export type ChecklistSummaryStats = {
    totalEligibleSessions: number;
    checklistSummary: { behavior: string; count: number }[];
};

export type ChecklistSummaryStatsKey = [string, ChecklistSummaryStatsParams, string | null];

export function useChecklistSummaryQuery({ params, ...rest }: ChecklistSummaryQueryProps) {
    const contentfulClient = useContentfulClient();

    return useQuery({
        queryKey: ["checklist-summary", params, userCache.employeeId],
        queryFn: (context) => getChecklistSummaryStats(contentfulClient, context),
        ...rest,
    });
}

export async function getChecklistSummaryStats(
    contentfulClient: ContentfulClientApi<undefined>,
    { queryKey: [, { adGroups, end, start }] }: QueryFunctionContext<ChecklistSummaryStatsKey>,
): Promise<ChecklistSummaryStats | undefined> {
    try {
        const behaviors = await buildBehaviorToTitleMap(contentfulClient, adGroups);

        const params = new URLSearchParams({
            start,
            end,
        });

        for (const behavior of behaviors.keys()) {
            params.append("behavior", behavior);
        }

        const result = await getGamificationApiClient()
            .get(`v1/metrics/checklist-summary?${params}`)
            .json<ChecklistSummaryStats>();

        return {
            ...result,
            checklistSummary: result.checklistSummary.map(({ behavior, ...rest }) => ({
                behavior: behaviors.get(behavior)!,
                ...rest,
            })),
        };
    } catch (error) {
        const logger = getLogger({ module: "checklist-summary-stats", supportTeam: "sales" });
        logger.error({ error }, "Error fetching checklist summary stats");
    }
}

/**
 * Query Contentful for sales checklists to get which behaviors are being tracked by Gaia in the `FullOfferChecklist`
 * component.
 *
 * @param contentfulClient
 * @param adGroups
 * @returns Map of behaviors keys (stored in the db) to the "title" which is the copy to be displayed in the checklist summary a part of
 * the expert dashboard
 */
async function buildBehaviorToTitleMap(
    contentfulClient: ContentfulClientApi<undefined>,
    adGroups: string[],
): Promise<Map<string, string>> {
    const behaviors = new Map<string, string>();

    for await (const { items } of paginateProductInfoByAdGroup(contentfulClient, { adGroups })) {
        items
            .flatMap((item) => item.fields.salesOfferChecklist?.fields.checklistSections)
            .flatMap((section) => section?.fields.checklistItems)
            .filter(isDefined)
            .map(({ fields: { key, title } }) => title && behaviors.set(key, title));
    }

    return behaviors;
}

function isDefined<T>(val?: T): val is Required<T> {
    return !!val;
}
